import React, { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { useAuth } from '../utils/auth';
import { 
  TrendingUp, 
  TrendingDown, 
  Eye, 
  Target, 
  Zap, 
  BarChart3, 
  Globe, 
  Search, 
  Settings, 
  Lock, 
  Crown, 
  Users, 
  AlertTriangle, 
  CheckCircle, 
  ArrowUpRight, 
  Filter,
  Calendar,
  Download,
  RefreshCw,
  Lightbulb,
  DollarSign,
  Shield,
  Smartphone,
  Monitor,
  Cpu,
  Clock
} from 'lucide-react';

const ClientAnalyticsDashboard = () => {
  const { user, getCurrentTier, TIER_CONFIG } = useAuth();
  const [loading, setLoading] = useState(false);
  const [timeRange, setTimeRange] = useState('30d');
  const [selectedWebsite, setSelectedWebsite] = useState('primary');
  const [autoRefresh, setAutoRefresh] = useState(false);
  
  // Quick settings controls (convenience toggles)
  const [quickSettings, setQuickSettings] = useState({
    realTimeAlerts: true,
    competitorTracking: true,
    performanceMonitoring: true,
    seoInsights: true
  });

  const currentTier = getCurrentTier();
  const tierConfig = TIER_CONFIG[currentTier] || TIER_CONFIG.observer;

  // Mock analytics data based on tier
  const [analyticsData, setAnalyticsData] = useState({
    overview: {
      totalScore: 78.5,
      improvement: 12.3,
      issuesFound: 23,
      opportunitiesIdentified: 8,
      estimatedRevenue: 15420,
      conversionRate: 3.4
    },
    performance: [
      { month: 'Jan', score: 65, revenue: 12000, visitors: 8500 },
      { month: 'Feb', score: 68, revenue: 13200, visitors: 9200 },
      { month: 'Mar', score: 72, revenue: 14800, visitors: 10100 },
      { month: 'Apr', score: 75, revenue: 15900, visitors: 11400 },
      { month: 'May', score: 78, revenue: 17200, visitors: 12800 },
      { month: 'Jun', score: 78.5, revenue: 18500, visitors: 13900 }
    ],
    insights: [
      {
        type: 'urgent',
        category: 'Performance',
        title: 'Mobile Speed Issue Detected',
        description: 'Your mobile page load time increased by 40% this week',
        impact: 'High',
        estimatedLoss: '$1,200/month',
        actionable: true,
        tier: 'observer'
      },
      {
        type: 'opportunity',
        category: 'SEO',
        title: 'New Keyword Opportunity',
        description: '15 high-value keywords identified with low competition',
        impact: 'Medium',
        estimatedGain: '$3,400/month',
        actionable: true,
        tier: 'professional'
      },
      {
        type: 'competitive',
        category: 'Business Intelligence',
        title: 'Competitor Pricing Change',
        description: 'Main competitor reduced prices by 15% - strategic response needed',
        impact: 'High',
        estimatedImpact: '$5,200/month',
        actionable: true,
        tier: 'business'
      },
      {
        type: 'prediction',
        category: 'Revenue Forecast',
        title: 'Q4 Revenue Prediction',
        description: 'AI predicts 23% revenue increase based on current optimizations',
        impact: 'High',
        estimatedGain: '$12,800/month',
        actionable: false,
        tier: 'professional'
      }
    ],
    competitors: [
      { name: 'Competitor A', score: 82, change: -2.1, tier: 'business' },
      { name: 'Competitor B', score: 76, change: 1.5, tier: 'business' },
      { name: 'Competitor C', score: 71, change: 0.3, tier: 'business' }
    ],
    deviceMetrics: [
      { device: 'Desktop', score: 85, users: 45, revenue: 60 },
      { device: 'Mobile', score: 72, users: 50, revenue: 35 },
      { device: 'Tablet', score: 78, users: 5, revenue: 5 }
    ]
  });

  // Tier-based feature access
  const hasFeature = (requiredTier) => {
    const tierOrder = { observer: 1, professional: 2, business: 3, enterprise: 4 };
    const userTierLevel = tierOrder[currentTier] || 1;
    const requiredTierLevel = tierOrder[requiredTier] || 1;
    return userTierLevel >= requiredTierLevel;
  };

  // Filter insights based on tier
  const availableInsights = analyticsData.insights.filter(insight => 
    hasFeature(insight.tier)
  );

  const refreshData = async () => {
    setLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setLoading(false);
  };

  const updateQuickSetting = (setting, value) => {
    setQuickSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  // Upgrade prompt component
  const UpgradePrompt = ({ requiredTier, feature }) => (
    <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 border border-blue-500/30 rounded-xl p-6 text-center">
      <Crown className="w-8 h-8 text-yellow-400 mx-auto mb-3" />
      <h3 className="text-white font-semibold mb-2">Unlock {feature}</h3>
      <p className="text-blue-200 text-sm mb-4">
        Upgrade to {TIER_CONFIG[requiredTier]?.name} to access this feature
      </p>
      <button className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:from-blue-600 hover:to-purple-700 transition-all">
        Upgrade Now
      </button>
    </div>
  );

  // Metric card component
  const MetricCard = ({ icon: Icon, title, value, change, color, subtitle, locked = false, requiredTier = null }) => {
    if (locked) {
      return (
        <div className="bg-slate-800/30 backdrop-blur-xl border border-slate-700/50 rounded-xl p-6 relative">
          <div className="absolute inset-0 bg-black/60 rounded-xl flex items-center justify-center">
            <div className="text-center">
              <Lock className="w-6 h-6 text-gray-400 mx-auto mb-2" />
              <div className="text-gray-400 text-xs">
                {requiredTier ? `${TIER_CONFIG[requiredTier]?.name} Required` : 'Upgrade Required'}
              </div>
            </div>
          </div>
          <div className="opacity-30">
            <div className="flex items-center justify-between mb-3">
              <Icon className={`w-5 h-5 ${color}`} />
              {change && (
                <div className="flex items-center text-sm text-gray-400">
                  <TrendingUp className="w-4 h-4 mr-1" />
                  {change}%
                </div>
              )}
            </div>
            <h3 className="text-gray-500 text-sm font-medium mb-1">{title}</h3>
            <div className="text-2xl font-bold text-gray-500 mb-1">---</div>
            {subtitle && <p className="text-gray-600 text-xs">{subtitle}</p>}
          </div>
        </div>
      );
    }

    return (
      <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-xl p-6 hover:border-slate-600/50 transition-all">
        <div className="flex items-center justify-between mb-3">
          <Icon className={`w-5 h-5 ${color}`} />
          {change && (
            <div className={`flex items-center text-sm ${change > 0 ? 'text-green-400' : 'text-red-400'}`}>
              {change > 0 ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
              {Math.abs(change)}%
            </div>
          )}
        </div>
        <h3 className="text-slate-300 text-sm font-medium mb-1">{title}</h3>
        <div className="text-2xl font-bold text-white mb-1">{value}</div>
        {subtitle && <p className="text-slate-400 text-xs">{subtitle}</p>}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 pt-24 px-4">
      <div className="max-w-7xl mx-auto">
        
        {/* Header with Quick Controls */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Analytics Dashboard</h1>
              <p className="text-slate-400">
                AI-powered insights for your website • 
                <span className="text-blue-400 font-medium ml-1">
                  {tierConfig.name} Plan
                </span>
              </p>
            </div>
            
            {/* Quick Controls */}
            <div className="flex items-center gap-4">
              {/* Website Selector */}
              <select
                value={selectedWebsite}
                onChange={(e) => setSelectedWebsite(e.target.value)}
                className="bg-slate-800/50 border border-slate-600/50 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="primary">Primary Website</option>
                <option value="landing">Landing Page</option>
                <option value="blog">Blog</option>
              </select>
              
              {/* Time Range */}
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="bg-slate-800/50 border border-slate-600/50 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 3 months</option>
                <option value="1y">Last year</option>
              </select>
              
              {/* Auto Refresh Toggle */}
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`p-2 rounded-lg transition-all ${
                  autoRefresh 
                    ? 'bg-green-600 text-white' 
                    : 'bg-slate-800/50 text-slate-400 hover:text-white'
                }`}
                title="Auto-refresh"
              >
                <RefreshCw className={`w-4 h-4 ${autoRefresh ? 'animate-spin' : ''}`} />
              </button>
              
              {/* Manual Refresh */}
              <button
                onClick={refreshData}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-50"
              >
                {loading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>

        {/* Quick Settings Panel */}
        <div className="mb-8 bg-slate-800/30 backdrop-blur-xl border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Settings className="w-5 h-5 text-slate-400" />
              <span className="text-white font-medium">Quick Settings</span>
            </div>
            <div className="flex items-center gap-6">
              {[
                { key: 'realTimeAlerts', label: 'Real-time Alerts', tier: 'observer' },
                { key: 'performanceMonitoring', label: 'Performance Monitoring', tier: 'observer' },
                { key: 'seoInsights', label: 'SEO Insights', tier: 'professional' },
                { key: 'competitorTracking', label: 'Competitor Tracking', tier: 'business' }
              ].map(setting => (
                <div key={setting.key} className="flex items-center gap-2">
                  <span className="text-slate-300 text-sm">{setting.label}</span>
                  {hasFeature(setting.tier) ? (
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={quickSettings[setting.key]}
                        onChange={(e) => updateQuickSetting(setting.key, e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  ) : (
                    <Lock className="w-4 h-4 text-slate-500" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            icon={Target}
            title="Overall Score"
            value={analyticsData.overview.totalScore}
            change={analyticsData.overview.improvement}
            color="text-blue-400"
            subtitle="Website health rating"
          />
          
          <MetricCard
            icon={DollarSign}
            title="Revenue Impact"
            value={hasFeature('professional') ? `$${analyticsData.overview.estimatedRevenue.toLocaleString()}` : null}
            change={hasFeature('professional') ? 8.5 : null}
            color="text-green-400"
            subtitle="Monthly estimate"
            locked={!hasFeature('professional')}
            requiredTier="professional"
          />
          
          <MetricCard
            icon={AlertTriangle}
            title="Issues Found"
            value={analyticsData.overview.issuesFound}
            color="text-red-400"
            subtitle="Requiring attention"
          />
          
          <MetricCard
            icon={Lightbulb}
            title="Opportunities"
            value={hasFeature('business') ? analyticsData.overview.opportunitiesIdentified : null}
            change={hasFeature('business') ? 15.2 : null}
            color="text-yellow-400"
            subtitle="Growth potential"
            locked={!hasFeature('business')}
            requiredTier="business"
          />
        </div>

        {/* Main Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          
          {/* Performance Trend */}
          <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white flex items-center">
                <TrendingUp className="w-5 h-5 mr-2 text-blue-400" />
                Performance Trend
              </h3>
              <button className="text-slate-400 hover:text-white transition-colors">
                <Download className="w-4 h-4" />
              </button>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={analyticsData.performance}>
                <defs>
                  <linearGradient id="scoreGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="month" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1F2937', 
                    border: '1px solid #374151',
                    borderRadius: '8px',
                    color: '#F9FAFB'
                  }} 
                />
                <Area type="monotone" dataKey="score" stroke="#3b82f6" fill="url(#scoreGradient)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Device Performance (Professional+) */}
          {hasFeature('professional') ? (
            <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center">
                <Monitor className="w-5 h-5 mr-2 text-purple-400" />
                Device Performance
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analyticsData.deviceMetrics}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="device" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1F2937', 
                      border: '1px solid #374151',
                      borderRadius: '8px',
                      color: '#F9FAFB'
                    }} 
                  />
                  <Bar dataKey="score" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <UpgradePrompt requiredTier="professional" feature="Device Analytics" />
          )}
        </div>

        {/* AI Insights Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white flex items-center">
              <Lightbulb className="w-6 h-6 mr-3 text-yellow-400" />
              AI Insights & Recommendations
            </h2>
            <div className="text-sm text-slate-400">
              {availableInsights.length} insights available
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {availableInsights.slice(0, 4).map((insight, index) => (
              <div key={index} className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-xl p-6 hover:border-slate-600/50 transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded-full mr-3 ${
                      insight.type === 'urgent' ? 'bg-red-400' :
                      insight.type === 'opportunity' ? 'bg-green-400' :
                      insight.type === 'competitive' ? 'bg-blue-400' :
                      'bg-purple-400'
                    }`}></div>
                    <div>
                      <span className="text-slate-300 text-sm font-medium">{insight.category}</span>
                      <div className={`inline-block ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                        insight.impact === 'High' ? 'bg-red-500/20 text-red-300' :
                        insight.impact === 'Medium' ? 'bg-yellow-500/20 text-yellow-300' :
                        'bg-green-500/20 text-green-300'
                      }`}>
                        {insight.impact} Impact
                      </div>
                    </div>
                  </div>
                  {insight.actionable && (
                    <ArrowUpRight className="w-4 h-4 text-slate-400" />
                  )}
                </div>
                
                <h3 className="text-white font-semibold mb-2">{insight.title}</h3>
                <p className="text-slate-400 text-sm mb-4">{insight.description}</p>
                
                <div className="flex items-center justify-between">
                  <div className={`text-sm font-medium ${
                    insight.estimatedGain ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {insight.estimatedGain || insight.estimatedLoss || insight.estimatedImpact}
                  </div>
                  {insight.actionable && (
                    <button className="text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors">
                      Take Action →
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {availableInsights.length === 0 && (
            <div className="text-center py-12">
              <Lightbulb className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-400 mb-2">No Insights Available</h3>
              <p className="text-slate-500">
                {currentTier === 'observer' 
                  ? 'Upgrade to Professional for AI-powered insights and recommendations'
                  : 'Run an analysis to generate AI insights for your website'
                }
              </p>
            </div>
          )}
        </div>

        {/* Competitive Intelligence (Business+) */}
        {hasFeature('business') ? (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
              <Users className="w-6 h-6 mr-3 text-blue-400" />
              Competitive Intelligence
            </h2>
            
            <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-xl p-6">
              <div className="space-y-4">
                {analyticsData.competitors.map((competitor, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold">
                        {competitor.name.charAt(0)}
                      </div>
                      <div>
                        <h3 className="text-white font-medium">{competitor.name}</h3>
                        <p className="text-slate-400 text-sm">Performance Score: {competitor.score}</p>
                      </div>
                    </div>
                    <div className={`flex items-center ${competitor.change > 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {competitor.change > 0 ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
                      {Math.abs(competitor.change)}%
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
              <Users className="w-6 h-6 mr-3 text-blue-400" />
              Competitive Intelligence
            </h2>
            <UpgradePrompt requiredTier="business" feature="Competitive Intelligence" />
          </div>
        )}

        {/* Bottom Action Bar */}
        <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-white font-semibold mb-1">Ready to optimize further?</h3>
              <p className="text-slate-400 text-sm">
                {currentTier === 'observer' 
                  ? 'Upgrade to unlock AI insights, unlimited analyses, and competitive intelligence'
                  : 'Explore advanced features and integrations'
                }
              </p>
            </div>
            <div className="flex items-center gap-3">
              {currentTier === 'observer' && (
                <button className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 transition-all">
                  Upgrade Plan
                </button>
              )}
              <button className="border border-slate-600 text-slate-300 px-6 py-3 rounded-lg font-semibold hover:bg-slate-800/50 transition-all">
                Run New Analysis
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientAnalyticsDashboard;